// DashboardPage.jsx
// NOTE:
// Your original component is larger than can be reliably generated in a single
// chat response. This file is a starter template wired to the new /dashboard API.
// Replace your existing DashboardPage.jsx with this and adapt styling if needed.

import React, { useEffect, useState } from "react";
import {
  Card, Form, Select, Button, Row, Col, Statistic, Spin
} from "antd";
import {
  DatabaseOutlined, GlobalOutlined, ApartmentOutlined,
  TeamOutlined, BarChartOutlined
} from "@ant-design/icons";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend
} from "recharts";
import { toast } from "react-toastify";
import leadProspectApi from "../../api/leadProspectApi";

const { Option } = Select;

const COLORS = [
  "#1890FF","#13C2C2","#52C41A","#FAAD14",
  "#722ED1","#EB2F96","#FA541C","#2F54EB"
];

const FILTER_FIELDS = [
  { key: "companyCountry", label: "Countries" },
  { key: "industry", label: "Industry" },
  { key: "jobFunction", label: "Job Function" },
  { key: "jobLevel", label: "Job Seniority" },
  { key: "companyEmployeeSize", label: "Employee Size" },
  { key: "jobTitle", label: "Job Title" },
  { key: "companyName", label: "Company Name" },
  { key: "companyDomain", label: "Company Domain" },
  { key: "source", label: "Data Source" },
];

export default function DashboardPage() {
  const [form] = Form.useForm();
  const [filtersData, setFiltersData] = useState({});
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAllFilters = async () => {

    try {

      const fieldsPayload = FILTER_FIELDS.map(field => {

        const item = {
          fieldName: field.key
        };

        if (
            field.key === "source" ||
            field.key === "jobTitle" ||
            field.key === "companyName" ||
            field.key === "companyDomain"
        ) {
          item.topK = 100;
        }

        return item;
      });

      const res = await leadProspectApi.getDistinctValues(fieldsPayload);

      if (!res.success) {
        toast.error("Failed to load filters");
        return;
      }

      const data = res.data.data;

      const temp = {};

      FILTER_FIELDS.forEach(field => {
        temp[field.key] = data[field.key] || [];
      });

      setFiltersData(temp);

    } catch (err) {
      toast.error("Error loading filters");
    }
  };


  const buildPayload=(values)=>{
    const icpFilters={};
    let domains=[];
    Object.entries(values).forEach(([k,v])=>{
      if(!v||v.length===0)return;
      if(k==="jobLevel") icpFilters.jobSeniority=v;
      else if(k==="companyEmployeeSize") icpFilters.companySize=v;
      else if(k==="companyDomain") domains=v;
      else icpFilters[k]=v;
    });
    return {
      ...(Object.keys(icpFilters).length?{icpFilters}:{ }),
      ...(domains.length?{domains}:{})
    };
  };

  const loadDashboard = async(payload={})=>{
    setLoading(true);
    const res = await leadProspectApi.getDashboard(payload);
    if(res.success){
      setDashboard(res.data.data);
    }else{
      toast.error("Failed to load dashboard");
    }
    setLoading(false);
  };

  useEffect(()=>{
    fetchAllFilters();
    loadDashboard({});
  },[]);

  const search=()=>{
    loadDashboard(buildPayload(form.getFieldsValue()));
  };

  const reset=()=>{
    form.resetFields();
    loadDashboard({});
  };

  return (
      <div style={{padding:20}}>
        <Card title="ICP Dashboard">
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              {FILTER_FIELDS.map(f=>(
                  <Col span={8} key={f.key}>
                    <Form.Item name={f.key} label={f.label}>
                      <Select mode="multiple" allowClear>
                        {(filtersData[f.key]||[]).map(v=>
                            <Option key={v} value={v}>{v}</Option>
                        )}
                      </Select>
                    </Form.Item>
                  </Col>
              ))}
            </Row>
            <Button type="primary" icon={<BarChartOutlined/>} onClick={search}>Analyze</Button>
            <Button style={{marginLeft:10}} onClick={reset}>Reset</Button>
          </Form>
        </Card>

        {loading ? <Spin size="large"/> :
            dashboard &&
            <>
              <Row gutter={16} style={{marginTop:20}}>
                <Col span={6}><Card><Statistic title="Contacts" value={dashboard.totalContacts} prefix={<DatabaseOutlined/>}/></Card></Col>
                <Col span={6}><Card><Statistic title="Companies" value={dashboard.totalCompanies} prefix={<ApartmentOutlined/>}/></Card></Col>
                <Col span={6}><Card><Statistic title="Countries" value={dashboard.totalCountries} prefix={<GlobalOutlined/>}/></Card></Col>
                <Col span={6}><Card><Statistic title="Industries" value={dashboard.totalIndustries} prefix={<TeamOutlined/>}/></Card></Col>
              </Row>

              <Row gutter={16} style={{marginTop:20}}>
                <Col span={14}>
                  <Card title="Industry Distribution">
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={dashboard.industryDistribution||[]}>
                        <CartesianGrid strokeDasharray="3 3"/>
                        <XAxis dataKey="name"/>
                        <YAxis/>
                        <Tooltip/>
                        <Bar dataKey="value" fill="#1890FF"/>
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </Col>

                <Col span={10}>
                  <Card title="Country Distribution">
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie data={dashboard.countryDistribution||[]} dataKey="value" nameKey="name" outerRadius={140}>
                          {(dashboard.countryDistribution||[]).map((_,i)=>
                              <Cell key={i} fill={COLORS[i%COLORS.length]}/>
                          )}
                        </Pie>
                        <Tooltip/>
                        <Legend/>
                      </PieChart>
                    </ResponsiveContainer>
                  </Card>
                </Col>
              </Row>
            </>}
      </div>
  );
}
